import { useEffect } from 'react';
import type { CombinedFieldType, ExtendedDynamicFormSettings } from '../../@types/forms';
import { isOfflineEnabled } from '../../db/odin-db-util';
import { useAppDispatch } from '../hooks';
import { fieldApi } from '../services/field';

const PERSON_FIELD_TYPES: CombinedFieldType[] = ['PERSONFIELD', 'MULTISELECTPERSONFIELD', 'READERS'];

const RECORD_FIELD_TYPES: CombinedFieldType[] = [
  'RECORD_OPTIONGROUP',
  'RECORD_COMBOBOX',
  'RECORD_MULTISELECTFIELD',
  'RECORD_MULTISELECTCHECKBOX',
];

const ARCHIVED_FILTER = { archived: { value: 'all', comparison: '=' } };

const usePrefetchFieldOptions = (formSettings?: ExtendedDynamicFormSettings) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!isOfflineEnabled) {
      return;
    }

    if (formSettings?.fields) {
      const allFields = formSettings.fields.flatMap((field) => {
        if (field.fieldType === 'GROUP') {
          return field.fields ?? [];
        }
        return field;
      });

      allFields.forEach((field) => {
        const fieldType = field.fieldType as CombinedFieldType;
        if (PERSON_FIELD_TYPES.includes(fieldType)) {
          prefetchPersonField(dispatch, Number(field.id));
        } else if (RECORD_FIELD_TYPES.includes(fieldType)) {
          prefetchRecordField(dispatch, Number(formSettings.id), Number(field.id), fieldType);
        } else if ('RECORD_TWINCOLUMNSELECT' === fieldType) {
          const subType = field.dynamicProperties?.subType as string;
          prefetchTwinColumnField(dispatch, Number(formSettings.id), Number(field.id), subType);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formSettings?.id]);
};

const prefetchPersonField = (dispatch: ReturnType<typeof useAppDispatch>, fieldId: number) => {
  dispatch(fieldApi.util.prefetch('personFieldOptions', { queryId: fieldId, limit: 50 }, { force: true }));
};

const prefetchRecordField = (
  dispatch: ReturnType<typeof useAppDispatch>,
  formId: number,
  fieldId: number,
  fieldType: CombinedFieldType
) => {
  const limit = fieldType === 'RECORD_COMBOBOX' ? 50 : 100;
  const needsArchivedFilter = fieldType === 'RECORD_OPTIONGROUP' || fieldType === 'RECORD_MULTISELECTCHECKBOX';

  dispatch(
    fieldApi.util.prefetch(
      'fieldOptions',
      {
        formId: Number(formId),
        queryId: Number(fieldId),
        limit,
        filters: needsArchivedFilter ? ARCHIVED_FILTER : undefined,
      },
      { force: true }
    )
  );
};

const prefetchTwinColumnField = (
  dispatch: ReturnType<typeof useAppDispatch>,
  formId: number,
  fieldId: number,
  subType?: string
) => {
  if ('TWINCOLUMNSELECT' === subType) {
    dispatch(
      fieldApi.util.prefetch('fieldOptions', { formId: Number(formId), queryId: fieldId, limit: 50 }, { force: true })
    );
  } else if ('TWINCOLUMNSELECTPERSONFIELD' === subType) {
    prefetchPersonField(dispatch, fieldId);
  }
};

export default usePrefetchFieldOptions;
